import React, { useCallback, useState } from 'react';
import { Box, Button, Heading, Text, useColorModeValue } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { getSimplifiedGatesFromStore } from '@store/feature-gates.store';
import {
  classifyError,
  extractComponentTree,
  formatRollbarTitle,
  sanitizeProps
} from '@util/helpers/error-boundary.helpers';

import buildInfo from '@root/build-helpers/build-info.json';

/**
 * Default UI component displayed when an error boundary catches an error.
 *
 * @param {object} props - Component props.
 * @param {Function} props.resetError - Function to call to attempt resetting the error state.
 * @param {string} [props.componentName='UI Component'] - Name of the component that errored.
 * @param {string} props.bgColor - Background color for the fallback UI.
 * @param {string} props.borderColor - Border color for the fallback UI.
 * @param {string} props.textColor - Text color for the fallback UI.
 * @param {boolean} [props.showRetry=true] - Whether to show the Retry/Reload buttons.
 * @returns {JSX.Element} The fallback UI component.
 */
function DefaultFallbackUI({
  // resetError,
  componentName,
  bgColor,
  borderColor,
  textColor,
  showRetry = true
}) {
  return (
    <Box
      bg={bgColor}
      borderRadius='md'
      p={4}
      my={2}
      border='1px solid'
      borderColor={borderColor}
    >
      <Heading size='sm'>Component Error in {componentName || 'UI Component'}</Heading>
      <Text
        fontSize='sm'
        mt={2}
        color={textColor}
      >
        This component encountered an error and couldn&apos;t be displayed.
      </Text>
      {showRetry && (
        <Button
          mt={3}
          size='sm'
          onClick={() => window.location.reload()}
        >
          Reload Page
        </Button>
      )}
    </Box>
  );
}

/**
 * Main Error Boundary component wrapper.
 * Uses Sentry's ErrorBoundary and provides custom context and fallback UI.
 *
 * @param {object} props - Component props passed to SentryErrorBoundary and DefaultFallbackUI.
 * @param {React.ReactNode} props.children - The components to wrap with the error boundary.
 * @param {string} [props.componentName] - The name of the wrapped component (often injected by plugin).
 * @param {'fatal'|'error'|'warning'|'info'|'debug'} [props.severity='error'] - The severity level for Sentry.
 * @param {React.ReactNode} [props.fallback] - Optional custom fallback component.
 * @param {boolean} [props.showRetry] - Whether the default fallback should show retry buttons.
 * @returns {JSX.Element} The Sentry ErrorBoundary wrapping the children.
 */
function ErrorBoundaryWrapper(props) {
  const bgColor = useColorModeValue('container.surfaceContainerLow', 'surface.surface');
  const borderColor = useColorModeValue('surface.outlineVariant', 'surface.outlineVariant');
  const textColor = useColorModeValue('surface.onSurfaceVariant', 'surface.onSurfaceVariant');
  const [errorState, setErrorState] = useState({ hasError: false, error: null });

  /**
   * Callback to render the fallback UI when an error is caught.
   * If a custom `props.fallback` is provided, it's used; otherwise, `DefaultFallbackUI` is rendered.
   * @param {object} params - Parameters provided by SentryErrorBoundary.
   * @param {Error} params.error - The error that was caught.
   * @param {Function} params.resetError - Function to reset the error boundary state.
   * @returns {React.ReactNode} The fallback UI to display.
   */
  const customFallbackUI = useCallback(
    ({ error, resetError }) => {
      if (!errorState.hasError) {
        setErrorState({ hasError: true, error });
      }

      if (props.fallback) {
        return props.fallback;
      }

      return (
        <DefaultFallbackUI
          error={error}
          resetError={() => {
            setErrorState({ hasError: false, error: null });
            resetError();
          }}
          componentName={props.componentName}
          bgColor={bgColor}
          borderColor={borderColor}
          textColor={textColor}
          showRetry={props.showRetry !== false}
        />
      );
    },
    [
      props.fallback,
      props.componentName,
      props.showRetry,
      bgColor,
      borderColor,
      textColor,
      errorState.hasError
    ]
  );

  /**
   * Function to generate extra context for Sentry reports.
   * Includes component name, severity, sanitized props, route information,
   * simplified feature flags, a component stack trace, and error classification.
   * @param {Sentry.Scope} scope - The Sentry scope to add context to.
   * @param {Error} originalError - The original error object.
   * @param {object} errorInfo - React error information object, containing componentStack.
   */
  const addSentryContext = (scope, originalError, errorInfo) => {
    const simplifiedFeatureFlags = getSimplifiedGatesFromStore() || {};

    // Set tags for filtering and grouping
    scope.setTag('component', props.componentName || 'Unknown component');
    scope.setTag('error_type', 'component_error');
    scope.setLevel(props.severity || 'error');

    // Set component context
    scope.setContext('component', {
      name: props.componentName || 'Unknown component',
      props: sanitizeProps(props),
      componentTree: extractComponentTree(errorInfo?.componentStack, 10),
      errorCategory: classifyError(originalError)
    });

    // Set route context
    scope.setContext('route', {
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      url: window.location.href
    });

    // Set feature flags context
    if (simplifiedFeatureFlags && Object.keys(simplifiedFeatureFlags).length > 0) {
      scope.setContext('feature_flags', simplifiedFeatureFlags);
    }

    // Set build info context
    scope.setContext('build_info', buildInfo);

    // Create a formatted error message for better grouping
    const formattedTitle = formatRollbarTitle(
      'Component',
      props.componentName || 'Unknown Component'
    );
    scope.setTag('formatted_title', formattedTitle);
  };

  return (
    <Sentry.ErrorBoundary
      fallback={customFallbackUI}
      beforeCapture={(scope, error, errorInfo) => {
        // Update error state
        setErrorState({ hasError: true, error });

        // Add rich context to Sentry
        addSentryContext(scope, error, errorInfo);
      }}
      onError={(error) => {
        // Additional error handling if needed
        setErrorState({ hasError: true, error });
      }}
    >
      {props.children}
    </Sentry.ErrorBoundary>
  );
}

export default ErrorBoundaryWrapper;
